import classnames from 'classnames';
import { useAppSelector } from 'hooks/useAppSelector';
import type { AnalyticCategoryActions } from 'models/analytics';
import { MouseEvent, useCallback, useRef, useState } from 'react';
import { Nav, Tab } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { postTradeTabChanged } from 'state/discover/page/page.actions';
import type { PostTradeTabId } from 'state/discover/page/page.model';
import { getSelectedPostTradeTab } from 'state/discover/page/page.selector';
import type { AppState } from 'state/state.model';
import { getLifecyclePortalUrl } from 'state/user';
import styled from 'styled-components';

import { LifeCycleList } from './life-cycle/list';

const LIFE_CYCLE = 'life-cycle';
const StyledNavLink = styled(Nav.Link)`
  color: var(--bs-secondary) !important;
  background-color: var(--bs-bg-lvl4);
  text-align: left;
  padding: var(--spacing-4);

  &.active {
    color: var(--bs-primary) !important;
    color: inherit;
    background-color: inherit;
  }
`;

function useAlreadyAccessedTabs<Tab extends string>(defaultTab: Tab): [(t: Tab) => boolean, (T: Tab) => void] {
  const [loadedTabs, setLoadedTabs] = useState<string[]>([defaultTab]);
  const isTabLoaded = useCallback((tab: Tab) => loadedTabs.includes(tab), [loadedTabs]);

  const markTabAsLoaded = useCallback(
    (tab: Tab) => {
      if (!isTabLoaded(tab)) {
        setLoadedTabs([...loadedTabs, tab]);
      }
    },
    [isTabLoaded, loadedTabs],
  );
  return [isTabLoaded, markTabAsLoaded];
}

const trackingActionMapping: Map<PostTradeTabId, AnalyticCategoryActions['Discover']> = new Map<
  PostTradeTabId,
  AnalyticCategoryActions['Discover']
>([[LIFE_CYCLE, 'Select Lifecycle tab']]);

export function PostTrade() {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeTabId = useAppSelector(getSelectedPostTradeTab);
  const dispatch = useDispatch();

  const [isTabLoaded, markTabAsLoaded] = useAlreadyAccessedTabs<PostTradeTabId>(activeTabId);
  const onTabClicked = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const newActiveTabId = (event.currentTarget.getAttribute('data-tab-id') as PostTradeTabId) || activeTabId;
      if (newActiveTabId !== activeTabId) {
        logAnalytics.trackEvent('Discover', trackingActionMapping.get(newActiveTabId)!);
        dispatch(postTradeTabChanged(newActiveTabId));
        markTabAsLoaded(newActiveTabId);
      }
    },
    [activeTabId, dispatch, markTabAsLoaded],
  );

  const lifecyclePortalUrl = useSelector((state: AppState) => getLifecyclePortalUrl(state)) || '#';

  return (
    <div className="post-trade bg-secondary bg-opacity-10" data-e2e="post-trade-container" ref={containerRef}>
      <Tab.Container defaultActiveKey={activeTabId}>
        <Nav className="nav-fill">
          <Nav.Item>
            <StyledNavLink
              className={classnames({
                active: activeTabId === LIFE_CYCLE,
                clickable: activeTabId !== LIFE_CYCLE,
              })}
              data-tab-id={LIFE_CYCLE as PostTradeTabId}
              data-e2e="life-cycle-tab"
              onClick={onTabClicked}
              tag="div"
            >
              <div className="d-flex justify-content-between align-items-center ">
                <h6 className="mb-0">
                  <FormattedMessage id="discover.lifeCycle.title" />{' '}
                </h6>
                <a
                  href={`${lifecyclePortalUrl}/portfolio`}
                  target="_lifecycle"
                  className="d-block btn-flat-secondary fw-medium"
                  data-e2e="life-cycle-portfolio-link"
                  onClick={e => {
                    e.stopPropagation(); // Prevent NavLink from capturing the click which blocks the navigation
                    trackOpenLifeCyclePortal();
                  }}
                >
                  <i className="icon icon-md">launch</i>
                </a>
              </div>
            </StyledNavLink>
          </Nav.Item>
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey={LIFE_CYCLE as PostTradeTabId} active={activeTabId === 'life-cycle'}>
            {isTabLoaded(LIFE_CYCLE) && <LifeCycleList />}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
}

function trackOpenLifeCyclePortal() {
  logAnalytics.trackEvent('Discover', 'Open Lifecycle portfolio');
}
